import { IPhoto } from '../../../types/files';
import { ICoordinate, ILineConfig, IPolygonConfig, IMarkerConfig } from '../types';

const MARKER_SIZE = 20;

interface MapColors {
  point: string;
  line: string;
  fill: string;
}

interface IMapPoint {
  id: string;
  title: string;
  coordinate: ICoordinate;
  images?: IPhoto[];
  comment?: string;
}

export class GeometryUtils {
  /**
   * Core function: calculates distance between consecutive coordinates
   * @param coordinates Array of coordinates to calculate distance for
   * @returns Total distance between all consecutive points
   */
  private static calculateCoordinatesDistance(coordinates: ICoordinate[]): number {
    if (coordinates.length < 2) return 0;

    let totalDistance = 0;
    for (let i = 1; i < coordinates.length; i++) {
      const prev = coordinates[i - 1];
      const curr = coordinates[i];

      const dx = curr.lng - prev.lng;
      const dy = curr.lat - prev.lat;
      totalDistance += Math.sqrt(dx * dx + dy * dy);
    }

    return totalDistance;
  }

  /**
   * Calculates total distance for a path of map points
   * @param points Array of map points
   * @returns Total distance of the path
   */
  static calculateDistance(points: IMapPoint[]): number {
    const coordinates = points.map(point => point.coordinate);
    return this.calculateCoordinatesDistance(coordinates);
  }

  /**
   * Calculates length of a line defined by coordinates
   * @param coordinates Array of coordinates defining the line
   * @returns Length of the line
   */
  static calculateLineLength(coordinates: ICoordinate[]): number {
    return this.calculateCoordinatesDistance(coordinates);
  }

  /**
   * Calculates distance between two specific coordinates
   * @param coord1 First coordinate
   * @param coord2 Second coordinate
   * @returns Distance between the two points
   */
  static calculatePointDistance(coord1: ICoordinate, coord2: ICoordinate): number {
    const dx = coord2.lng - coord1.lng;
    const dy = coord2.lat - coord1.lat;
    return Math.sqrt(dx * dx + dy * dy);
  }

  /**
   * Calculates perimeter of a polygon
   * @param points Array of map points forming the polygon
   * @returns Perimeter of the polygon
   */
  static calculatePolygonPerimeter(points: IMapPoint[]): number {
    if (points.length < 3) return 0;

    // Close the polygon by adding the first point at the end
    const coordinates = [...points.map(p => p.coordinate), points[0].coordinate];
    return this.calculateCoordinatesDistance(coordinates);
  }

  static calculateAffectedLines(
    markerId: string,
    oldCoordinate: ICoordinate,
    newCoordinate: ICoordinate,
    points: IMapPoint[],
    colors: MapColors
  ): Array<{ oldLine: ILineConfig; newLine: ILineConfig }> {
    const affectedLines: Array<{
      oldLine: ILineConfig;
      newLine: ILineConfig;
    }> = [];
    const markerIndex = points.findIndex(p => p.id === markerId);

    if (markerIndex === -1) return affectedLines;

    if (markerIndex > 0) {
      const prevPoint = points[markerIndex - 1];
      const lineId = `line-${prevPoint.id}-${markerId}`;

      const oldLine: ILineConfig = {
        id: lineId,
        coordinates: [prevPoint.coordinate, oldCoordinate],
        color: colors.line,
        width: 3,
      };

      const newLine: ILineConfig = {
        id: lineId,
        coordinates: [prevPoint.coordinate, newCoordinate],
        color: colors.line,
        width: 3,
      };

      affectedLines.push({ oldLine, newLine });
    }

    if (markerIndex < points.length - 1) {
      const nextPoint = points[markerIndex + 1];
      const lineId = `line-${markerId}-${nextPoint.id}`;

      const oldLine: ILineConfig = {
        id: lineId,
        coordinates: [oldCoordinate, nextPoint.coordinate],
        color: colors.line,
        width: 3,
      };

      const newLine: ILineConfig = {
        id: lineId,
        coordinates: [newCoordinate, nextPoint.coordinate],
        color: colors.line,
        width: 3,
      };

      affectedLines.push({ oldLine, newLine });
    }

    return affectedLines;
  }

  static createPolygonConfig(
    points: IMapPoint[],
    colors: MapColors,
    id: string = 'question-polygon'
  ): IPolygonConfig {
    return {
      id,
      coordinates: points.map(p => p.coordinate),
      fillColor: colors.fill,
      strokeColor: colors.line,
      fillOpacity: 0.3,
      strokeOpacity: 1,
      strokeWidth: 2,
    };
  }

  static createMarkerConfig(point: IMapPoint, index: number, colors: MapColors): IMarkerConfig {
    return {
      id: point.id,
      coordinate: point.coordinate,
      title: `${index + 1}`,
      color: colors.point,
      size: MARKER_SIZE,
      draggable: true,
    };
  }

  static createLineConfigs(points: IMapPoint[], colors: MapColors): ILineConfig[] {
    const lines: ILineConfig[] = [];

    for (let i = 1; i < points.length; i++) {
      const prevPoint = points[i - 1];
      const currentPoint = points[i];

      lines.push({
        id: `line-${prevPoint.id}-${currentPoint.id}`,
        coordinates: [prevPoint.coordinate, currentPoint.coordinate],
        color: colors.line,
        width: 3,
      });
    }

    return lines;
  }

  static createRemovedLinesForPoint(
    markerIndex: number,
    selectedMarker: IMarkerConfig,
    points: IMapPoint[],
    colors: MapColors
  ): ILineConfig[] {
    const removedLines: ILineConfig[] = [];

    if (markerIndex > 0) {
      const prevPoint = points[markerIndex - 1];
      removedLines.push({
        id: `line-${prevPoint.id}-${selectedMarker.id}`,
        coordinates: [prevPoint.coordinate, selectedMarker.coordinate],
        color: colors.line,
        width: 3,
      });
    }

    if (markerIndex < points.length - 1) {
      const nextPoint = points[markerIndex + 1];
      removedLines.push({
        id: `line-${selectedMarker.id}-${nextPoint.id}`,
        coordinates: [selectedMarker.coordinate, nextPoint.coordinate],
        color: colors.line,
        width: 3,
      });
    }

    return removedLines;
  }

  static createNewLinesForPointRemoval(
    markerIndex: number,
    points: IMapPoint[],
    colors: MapColors
  ): ILineConfig[] {
    const newLines: ILineConfig[] = [];

    if (markerIndex > 0 && markerIndex < points.length - 1) {
      const prevPoint = points[markerIndex - 1];
      const nextPoint = points[markerIndex + 1];
      newLines.push({
        id: `line-${prevPoint.id}-${nextPoint.id}`,
        coordinates: [prevPoint.coordinate, nextPoint.coordinate],
        color: colors.line,
        width: 3,
      });
    }

    return newLines;
  }
}
